import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import {
  MenuBook as MenuBookIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Language as LanguageIcon,
  Group as GroupIcon,
  Verified as VerifiedIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const BusinessGlossaryFeature: React.FC = () => {
  const navigate = useNavigate();

  const benefits = [
    'Consistent terminology across all DHS components',
    'Reduced miscommunication and data interpretation errors',
    'Faster onboarding for new team members',
    'Improved collaboration between technical and business teams',
    'Standardized definitions for regulatory compliance',
    'Enhanced data quality through common understanding'
  ];

  const capabilities = [
    {
      icon: <SearchIcon sx={{ color: 'primary.main' }} />,
      title: 'Smart Search',
      description: 'Quickly find terms and definitions with intelligent search capabilities'
    },
    {
      icon: <LanguageIcon sx={{ color: 'primary.main' }} />,
      title: 'Multi-Language Support',
      description: 'Support for multiple languages and regional terminology variations'
    },
    {
      icon: <GroupIcon sx={{ color: 'primary.main' }} />,
      title: 'Collaborative Editing',
      description: 'Subject matter experts can propose and review term definitions'
    },
    {
      icon: <VerifiedIcon sx={{ color: 'primary.main' }} />,
      title: 'Version Control',
      description: 'Track changes and maintain history of term definitions over time'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3, color: 'primary.main' }}
      >
        Back
      </Button>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <MenuBookIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
          <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Business Glossary
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Standardized terminology and definitions for business concepts across the organization
        </Typography>
        
        {/* Access Application Button */}
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/business-glossary')}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              bgcolor: 'primary.dark'
            }
          }}
        >
          Access Business Glossary Application →
        </Button>
      </Box>

      {/* Overview */}
      <Paper sx={{ p: 4, mb: 4, borderLeft: '4px solid #003366' }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          Overview
        </Typography>
        <Typography paragraph>
          The Business Glossary provides a centralized repository of standardized business terms and definitions used across DHS components. It ensures that everyone in the organization speaks the same language when discussing data, processes, and business concepts.
        </Typography>
        <Typography paragraph>
          By establishing a common vocabulary, the Business Glossary eliminates ambiguity, reduces miscommunication, and enables more effective collaboration between technical and business teams. It serves as the authoritative source for understanding what terms mean in the context of DHS operations.
        </Typography>
      </Paper>

      {/* Key Benefits */}
      <Paper sx={{ p: 4, mb: 4, bgcolor: '#f8f9fa' }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          Key Benefits
        </Typography>
        <List>
          {benefits.map((benefit, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText primary={benefit} />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
        Core Capabilities
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {capabilities.map((capability, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', borderLeft: '4px solid #003366' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {capability.icon}
                  <Typography variant="h6" sx={{ ml: 1, color: 'primary.main', fontWeight: 600 }}>
                    {capability.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {capability.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Use Cases */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
          Common Use Cases
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Data Governance
          </Typography>
          <Typography paragraph>
            Establish clear definitions for data elements, ensuring compliance with policies and regulations across all DHS components.
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Cross-Component Collaboration
          </Typography>
          <Typography paragraph>
            Enable seamless communication between different DHS agencies by providing a shared understanding of key business terms.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Training & Onboarding
          </Typography>
          <Typography paragraph>
            Accelerate new employee onboarding by providing easy access to standardized definitions and organizational terminology.
          </Typography>
        </Box>
      </Paper>

      {/* CTA */}
      <Paper sx={{ p: 4, bgcolor: 'primary.main', color: 'white', textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Ready to Standardize Your Business Terminology?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Learn more about how E-Unify's Business Glossary can help your organization
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/contact')}
          sx={{
            bgcolor: 'white',
            color: 'primary.main',
            '&:hover': { bgcolor: '#f8f9fa' }
          }}
        >
          Contact Us
        </Button>
      </Paper>
    </Container>
  );
};

export default BusinessGlossaryFeature;
